import { useState } from 'react';
import { Check, ExternalLink, FolderOpen, Loader2, Mail } from 'lucide-react';
import type { CoverEmailDraft } from '@officehub/shared';
import type { ReportWizardApi } from './types';
import { CARD, INPUT, PRIMARY, SECONDARY, TINT } from './ui';
import { httpsUrlOrNull } from './safeUrl';

/** The server refuses to send a PDF older than the latest edits unless told to. */
const STALE_MARKER = 'older than the latest draft edits';

interface Props {
  isOnline: boolean;
  hasLead: boolean;
  cover?: CoverEmailDraft;
  api: Pick<ReportWizardApi, 'send'>;
  isSavingDrive: boolean;
  drive: { driveFileUrl: string; driveFolderUrl: string } | null;
  onSaveDrive(): void;
  onRegenerate(): void;
  onError(message: string): void;
  onSent(recipient: string): void;
  onDone(): void;
  form: DeliverState;
}

/**
 * The email form's state. It lives in the wizard, not the card, because the card
 * unmounts while the PDF regenerates: an edited cover message and the "sent" state
 * must survive that.
 */
export function useDeliverState() {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [stale, setStale] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  /** False once the PDF is regenerated after sending: the new PDF has not gone out. */
  const [sentIsCurrent, setSentIsCurrent] = useState(false);
  return { open, setOpen, to, setTo, name, setName, body, setBody, stale, setStale, sentTo, setSentTo, sentIsCurrent, setSentIsCurrent };
}
export type DeliverState = ReturnType<typeof useDeliverState>;

export function DeliverCard(p: Props) {
  const { open, setOpen, to, setTo, name, setName, body, setBody, stale, setStale, sentTo, setSentTo, sentIsCurrent, setSentIsCurrent } = p.form;
  const [sending, setSending] = useState(false);

  const openForm = () => {
    setTo((v) => v || p.cover?.recipientEmail || '');
    setName((v) => v || p.cover?.recipientName || '');
    setBody((v) => v || p.cover?.body || '');
    setStale(false);
    setOpen(true);
  };

  const send = async (allowStale = false) => {
    if (!to.trim()) return;
    setSending(true);
    setStale(false);
    try {
      const { recipient } = await p.api.send({
        recipientEmail: to.trim(),
        ...(name.trim() ? { recipientName: name.trim() } : {}),
        ...(body.trim() ? { coverBody: body.trim() } : {}),
        ...(allowStale ? { allowStale: true } : {}),
      });
      setSentTo(recipient);
      setSentIsCurrent(true);
      setOpen(false);
      p.onSent(recipient);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Send failed';
      if (message.includes(STALE_MARKER)) setStale(true);
      else p.onError(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className={CARD}>
        {p.drive ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700">
            <Check size={16} /> Saved to Drive
            {httpsUrlOrNull(p.drive.driveFolderUrl) && (
              <a href={httpsUrlOrNull(p.drive.driveFolderUrl) ?? undefined} target="_blank" rel="noopener noreferrer" className={`${TINT} ml-auto`}>
                Open <ExternalLink size={13} />
              </a>
            )}
          </div>
        ) : (
          <button type="button" onClick={p.onSaveDrive} disabled={!p.isOnline || !p.hasLead || p.isSavingDrive} className={`${PRIMARY} min-h-[52px] w-full`}>
            {p.isSavingDrive ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : <><FolderOpen size={16} /> Save to Lead&apos;s Drive Folder</>}
          </button>
        )}
      </div>

      {sentTo && !open && (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {sentIsCurrent
            ? <><Check size={16} /> Report sent to {sentTo}</>
            : <>An earlier version of this report was sent to {sentTo}. This PDF has not been sent.</>}
        </div>
      )}

      {open ? (
        <div className={`${CARD} space-y-3`}>
          <h4 className="text-sm font-bold text-slate-900">Email Report to Client</h4>
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Recipient email" className={INPUT} />
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Recipient name (optional)" className={INPUT} />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000} rows={5}
            placeholder="Cover message (prefilled from the AI draft — edit freely)" className={INPUT} />
          {stale && (
            <div className="space-y-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <p>The PDF is older than your latest edits — regenerate it before sending.</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => { setOpen(false); p.onRegenerate(); }} disabled={!p.isOnline} className={SECONDARY}>Regenerate PDF</button>
                <button type="button" onClick={() => void send(true)} disabled={!p.isOnline || sending} className={SECONDARY}>Send anyway</button>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => void send()} disabled={!p.isOnline || sending || !to.trim()} className={`${PRIMARY} min-h-[48px] flex-1`}>
              {sending ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Mail size={16} /> Send Report</>}
            </button>
            <button type="button" onClick={() => setOpen(false)} className={`${SECONDARY} min-h-[48px]`}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={openForm} disabled={!p.isOnline} className={`${SECONDARY} min-h-[52px] w-full`}>
          <Mail size={16} /> {sentTo ? 'Send Again' : 'Email Report to Client'}
        </button>
      )}

      <button type="button" onClick={p.onDone} className={`${SECONDARY} w-full`}>Done</button>
    </div>
  );
}
